/**
 * Enum for K8ProgramStatusRouter view states
 *
 * This enum defines the possible states that the K8ProgramStatusRouter component
 * can be in, which determines what view should be rendered.
 */
export const K8ProgramStatus = {
  // User is missing teacher preferences and they must be set
  MISSING_TEACHER_PREFERENCES: 'MISSING_TEACHER_PREFERENCES',

  // User has preferences set but has not completed training or created a class
  // Training is required for this user
  PREFERENCES_SET_TRAINING_REQUIRED_NO_CLASS:
    'PREFERENCES_SET_TRAINING_REQUIRED_NO_CLASS',

  // User has preferences set but has not completed training or created a class
  // Training is NOT required for this user (experienced teacher)
  PREFERENCES_SET_TRAINING_NOT_REQUIRED_NO_CLASS:
    'PREFERENCES_SET_TRAINING_NOT_REQUIRED_NO_CLASS',

  // User has completed some training but not all, and has not created a class
  PREFERENCES_SET_SOME_TRAINING_NO_CLASS:
    'PREFERENCES_SET_SOME_TRAINING_NO_CLASS',

  // User has completed all training but not created a class
  TRAINING_COMPLETE_NO_CLASS: 'TRAINING_COMPLETE_NO_CLASS',

  // User has not completed training and created a class
  // Training is required for this user
  TRAINING_REQUIRED_INCOMPLETE_CLASS_CREATED:
    'TRAINING_REQUIRED_INCOMPLETE_CLASS_CREATED',

  // User has not completed training and created a class
  // Training is NOT required for this user (experienced teacher)
  TRAINING_NOT_REQUIRED_INCOMPLETE_CLASS_CREATED:
    'TRAINING_NOT_REQUIRED_INCOMPLETE_CLASS_CREATED',

  // User has completed all training and created a class
  TRAINING_COMPLETE_CLASS_CREATED: 'TRAINING_COMPLETE_CLASS_CREATED',
}

/**
 * Helper function to determine the current program status based on user data
 *
 * @param {Object} params - Parameters object
 * @param {boolean} params.hasTeacherPreferences - Whether the user has set teacher preferences
 * @param {boolean} params.hasCompletedTraining - Whether the user has completed all training
 * @param {boolean} params.hasCreatedClass - Whether the user has created a class
 * @param {boolean} params.someTrainingCompleted - Whether the user has completed some training
 * @param {boolean} params.requiredTraining - Whether training is required for this user
 * @returns {string} The current program status from the K8ProgramStatus enum
 */
export const determineK8ProgramStatus = ({
  hasTeacherPreferences = false,
  hasCompletedTraining = false,
  hasCreatedClass = false,
  someTrainingCompleted = false,
  requiredTraining = true,
} = {}) => {
  // If user hasn't set preferences, that's the first priority
  if (!hasTeacherPreferences) {
    return K8ProgramStatus?.MISSING_TEACHER_PREFERENCES
  }

  // User has preferences set, now check training and class status
  if (!hasCreatedClass) {
    // No class created yet
    if (hasCompletedTraining) {
      // All training complete, no class
      return K8ProgramStatus?.TRAINING_COMPLETE_NO_CLASS
    } else if (someTrainingCompleted) {
      // Some training complete, no class
      return K8ProgramStatus?.PREFERENCES_SET_SOME_TRAINING_NO_CLASS
    } else if (requiredTraining) {
      // No training complete, training required, no class
      return K8ProgramStatus?.PREFERENCES_SET_TRAINING_REQUIRED_NO_CLASS
    } else {
      // No training complete, training not required, no class
      return K8ProgramStatus?.PREFERENCES_SET_TRAINING_NOT_REQUIRED_NO_CLASS
    }
  } else {
    // Class has been created
    if (hasCompletedTraining) {
      // All training complete, class created
      return K8ProgramStatus?.TRAINING_COMPLETE_CLASS_CREATED
    } else if (requiredTraining) {
      // Training incomplete, training required, class created
      return K8ProgramStatus?.TRAINING_REQUIRED_INCOMPLETE_CLASS_CREATED
    } else {
      // Training incomplete, training not required, class created
      return K8ProgramStatus?.TRAINING_NOT_REQUIRED_INCOMPLETE_CLASS_CREATED
    }
  }
}

export default K8ProgramStatus
